import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faClockRotateLeft, faCopy, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import {
  faCaretDown as faCaretDownRegular,
  faClockRotateLeft as faClockRotateLeftRegular,
  faCopy as faCopyRegular,
  faPaperPlane as faPaperPlaneRegular
} from '@fortawesome/pro-regular-svg-icons';
import { DropdownMenu } from '@buildoutinc/blueprint-react/ui/DropdownMenu';

import InviteActivityModal from 'components/documents/invite_activity_modal';
import ShareLinkByEmailModal from 'components/documents/share_link_by_email_modal';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';

const SHARE_DOC_MODAL_DESCRIPTION = 'Easily invite your lead to view your locked documents.';
const WITH_GATES =
  ` Once sent, the invitee will adhere to the CA & Access settings you've configured for your documents.`;
const WITHOUT_GATES = ' The invitee will skip the waitlist.';
const SOLID_ICONS = { caret: faCaretDown, copy: faCopy, history: faClockRotateLeft, invite: faPaperPlane };
const REGULAR_ICONS = {
  caret: faCaretDownRegular, copy: faCopyRegular, history: faClockRotateLeftRegular, invite: faPaperPlaneRegular
};

export default function InviteToViewMenu({
  compact, features, lead, menuContainer, project, refreshLead, shareLinkSide, showUpdateLeadModal, useVaultGates
}) {
  const [showShareByEmailModal, setShowShareByEmailModal] = useState(false);
  const [showInviteActivity, setShowInviteActivity] = useState(false);
  const shareLinks = (lead.shareLinks || []).filter(link => !shareLinkSide || link.label === shareLinkSide);
  const icons = compact ? REGULAR_ICONS : SOLID_ICONS;

  // The lead_invite_activity customization gates only what the broker sees.
  // SendGrid events are recorded for every company regardless, so a company
  // switched on later already has the history behind this UI.
  const showInviteActivityUi = Boolean(features.leadInviteActivity && lead.inviteActivity);

  const openShareByEmailModal = () => setShowShareByEmailModal(true);
  const hideShareByEmailModal = () => {
    setShowShareByEmailModal(false);
    refreshLead(lead);
  };

  const inviteButton = (
    <Button
      icon={icons.invite}
      iconEnd={icons.caret}
      label={compact ? 'Invite to View' : 'Invite to view'}
      variant="outline"
    />
  );

  return (
    <React.Fragment>
      <DropdownMenu>
        <DropdownMenu.Trigger render={inviteButton}/>
        <DropdownMenu.Content align={compact ? 'end' : 'start'} container={menuContainer}>
          <DropdownMenu.Item onClick={openShareByEmailModal}>
            <FontAwesomeIcon icon={icons.invite}/> Invite to view
          </DropdownMenu.Item>
          {shareLinks.map(({ label, url }) => (
            <CopyInviteLinkMenuItem
              icon={icons.copy}
              key={label}
              label={shareLinkSide || shareLinks.length > 1 ? `Copy ${label} invite link` : 'Copy invite link'}
              url={url}
            />
          ))}
          {showInviteActivityUi ? (
            <DropdownMenu.Item onClick={() => setShowInviteActivity(true)}>
              <FontAwesomeIcon icon={icons.history}/> View invite activity
            </DropdownMenu.Item>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu>

      {/* Keyed on the email because the modal seeds its form from `email` only on
          mount; without a remount, "Try again" after "Edit email address" would
          resend to the old (bounced) address. */}
      <ShareLinkByEmailModal
        description={`${SHARE_DOC_MODAL_DESCRIPTION}${useVaultGates ? WITH_GATES : WITHOUT_GATES}`}
        email={lead.leadData.email}
        fromEmails={project.brokersForShareDocs}
        hideModal={hideShareByEmailModal}
        key={lead.leadData.email}
        leadId={lead.id}
        propertyId={project.id}
        show={showShareByEmailModal}
        title="Invite to View documents"
      />

      {showInviteActivityUi ? (
        <InviteActivityModal
          lead={lead}
          show={showInviteActivity}
          onEditEmail={() => {
            setShowInviteActivity(false);
            showUpdateLeadModal(lead);
          }}
          onHide={() => setShowInviteActivity(false)}
          onRetry={() => {
            // Reopens the send modal rather than firing a silent resend, so the
            // broker can correct the address before trying again.
            setShowInviteActivity(false);
            openShareByEmailModal();
          }}
        />
      ) : null}
    </React.Fragment>
  );
}

InviteToViewMenu.propTypes = {
  compact: PropTypes.bool,
  features: PropTypes.shape({
    leadInviteActivity: PropTypes.bool
  }).isRequired,
  lead: PropTypes.shape({
    id: PropTypes.number.isRequired,
    inviteActivity: PropTypes.object,
    leadData: PropTypes.shape({ email: PropTypes.string }).isRequired,
    shareLinks: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    }))
  }).isRequired,
  menuContainer: PropTypes.instanceOf(Element),
  project: PropTypes.shape({
    brokersForShareDocs: PropTypes.array,
    id: PropTypes.number.isRequired
  }).isRequired,
  refreshLead: PropTypes.func.isRequired,
  shareLinkSide: PropTypes.oneOf(['Lease', 'Sale']),
  showUpdateLeadModal: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool
};

export function CopyInviteLinkMenuItem({ icon = faCopy, label, url }) {
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(url);
    showSnackbar({ text: 'Link has been copied to your clipboard', variant: 'success' });
  };

  return (
    <DropdownMenu.Item onClick={copyLinkToClipboard}>
      <FontAwesomeIcon icon={icon}/> {label}
    </DropdownMenu.Item>
  );
}

CopyInviteLinkMenuItem.propTypes = {
  icon: PropTypes.object,
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
};
